import React from 'react';
import { Autocomplete, Divider, FormControl, InputAdornment, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDateTimePicker, DesktopDateTimePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import EuroIcon from '@mui/icons-material/Euro';
import PercentIcon from '@mui/icons-material/Percent';
import ClearIcon from '@mui/icons-material/Clear';
import CostsType from '../components/CostsType';
import { GetLabel } from '../../other/ConfigBasic';
import theme from '../../../themes/theme';
import 'dayjs/locale/de';
import i18n from 'i18next';

function ReceiptForm({ form, costCentreList, costObjectList, variant = 'mobile' }) {
    const { t } = useTranslation();
    const isMobile = variant === 'mobile';
    const DateTimePicker = isMobile ? MobileDateTimePicker : DesktopDateTimePicker;

    const {
        dtBilling, setDtBilling,vendor,setVendor,addressChange,setAddressChange,number,setNumber,category,setCategory,
        total,setTotal,vat,setVat,type,setType,quantity,setQuantity,unitPrice,costCentre,setCostCentre,
        costObject,setCostObject,selectedOption,setSelectedOption,validateError,options,categories,typeUnits,
        userCleared,setUserCleared,handleOptionChange,inputStylePhone,beitraginputStylePhone,
    } = form;

    const inputStyles = isMobile
        ? {
              primary: inputStylePhone,
              secondary: beitraginputStylePhone,
          }
        : {
              primary: {
                  height: '48px',
                  input: {
                      height: '100%',
                      fontSize: '14px',
                      fontFamily: 'CircularProMedium',
                  },
                  label: {
                      height: '100%',
                      fontSize: '14px',
                      fontFamily: 'CircularProMedium',
                  },
              },
              secondary: {
                  width: '244px',
                  height: '48px',
                  input: {
                      height: '100%',
                      fontSize: '14px',
                      fontFamily: 'CircularProBook',
                  },
                  label: {
                      fontSize: '14px',
                      fontFamily: 'CircularProBook',
                  },
              },
          };

    return (
        <LocalizationProvider adapterLocale={i18n.language.includes('de') ? 'de' : ''} dateAdapter={AdapterDayjs}>
            <Stack spacing={isMobile ? 4 : 4} sx={isMobile ? { width: '100%' } : {}}>
                <TextField
                    sx={inputStyles.primary.input}
                    label={t('provider')}
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    required
                    error={validateError.vendor}
                    helperText={validateError.vendor ? t('mandatoryField') : ''}
                />
                <Autocomplete
                    id='country-customized-option-demo'
                    options={options}
                    isOptionEqualToValue={(option, value) => option.placeId === value.placeId}
                    getOptionLabel={(option) => (option.label ? option.label : addressChange)}
                    value={selectedOption === null ? addressChange : selectedOption}
                    noOptionsText={t('enterAddress')}
                    onChange={handleOptionChange}
                    onBlur={() => {
                        if (!selectedOption && options.length > 0 && !userCleared) {
                            setSelectedOption(options[0]);
                            setAddressChange(options[0].label);
                        }
                    }}
                    autoSelect={true}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: null,
                            }}
                            label={t('addressSearch')}
                            onChange={(e) => {
                                setAddressChange(e.target.value);
                                if (!e.target.value) {
                                    setSelectedOption(null);
                                    setUserCleared(true);
                                } else {
                                    setUserCleared(false);
                                }
                            }}
                            value={addressChange}
                            required
                            error={validateError.address}
                            helperText={validateError.address ? t('mandatoryField') : ''}
                        />
                    )}
                />
                <DateTimePicker
                    label={t('date')}
                    inputFormat='DD.MM.YYYY HH:mm'
                    value={dtBilling}
                    onChange={(value) => setDtBilling(value.$d)}
                    renderInput={(params) => {
                        return <TextField sx={inputStyles.primary} {...params} />;
                    }}
                    views={['day', 'month', 'year', 'hours', 'minutes']}
                    required={isMobile}
                />
                <TextField
                    sx={inputStyles.primary.input}
                    label={t('receiptNumber')}
                    onChange={(e) => setNumber(e.target.value)}
                    required
                    value={number}
                    error={validateError.number}
                    helperText={validateError.number ? t('mandatoryField') : ''}
                />
            </Stack>

            <Divider sx={{ width: isMobile ? '90%' : '100%' }} />

            <Stack spacing={4} sx={{ width: '100%' }}>
                <CostsType
                    category={category}
                    setCategory={setCategory}
                    label={t('costType')}
                    fuelState={type}
                    setFuelState={setType}
                    validateError={validateError}
                />
                {(costCentreList.length > 0 || costObjectList.length > 0) && (
                    <>
                        <FormControl fullWidth>
                            <InputLabel id='costCentre-select-label'>{t('costCentre')}</InputLabel>
                            <Select
                                endAdornment={
                                    costCentre.length > 0 && (
                                        <ClearIcon
                                            sx={{
                                                cursor: 'pointer',
                                                backgroundColor: '#fff',
                                                zIndex: 1,
                                                color: theme.palette.grey[900],
                                                marginRight: '-7px',
                                            }}
                                            onClick={() => {
                                                setCostCentre('');
                                            }}
                                        />
                                    )
                                }
                                labelId='costCentre-select-label'
                                id='costCentre-select'
                                value={costCentre === '' ? costCentre : costCentre[0]}
                                label={t('costCentre')}
                                onChange={(e) => setCostCentre([e.target.value])}
                                sx={{ height: '48px' }}
                            >
                                {costCentreList.map((costCentreItem) => (
                                    <MenuItem key={costCentreItem.id} value={costCentreItem.name}>
                                        {costCentreItem.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id='costObject-select-label'>{t('costObject')}</InputLabel>
                            <Select
                                endAdornment={
                                    costObject.length > 0 && (
                                        <ClearIcon
                                            sx={{
                                                cursor: 'pointer',
                                                backgroundColor: '#fff',
                                                zIndex: 1,
                                                color: theme.palette.grey[900],
                                                marginRight: '-7px',
                                            }}
                                            onClick={() => {
                                                setCostObject('');
                                            }}
                                        />
                                    )
                                }
                                labelId='costObject-select-label'
                                id='costObject-select'
                                value={costObject === '' ? costObject : costObject[0]}
                                label={t('costObject')}
                                onChange={(e) => setCostObject([e.target.value])}
                                sx={{ height: '48px' }}
                            >
                                {costObjectList.map((costObjectItem) => (
                                    <MenuItem key={costObjectItem.id} value={costObjectItem.name}>
                                        {costObjectItem.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </>
                )}
                <Stack direction='row' spacing={isMobile ? 1.5 : 3} justifyContent={isMobile ? 'center' : 'space-between'}>
                    <TextField
                        sx={inputStyles.secondary}
                        label={t('grossAmount')}
                        onChange={(e) => setTotal(e.target.value)}
                        value={total}
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <EuroIcon
                                        sx={{
                                            width: '20px',
                                            aspectRatio: 1,
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                        error={validateError.total}
                        helperText={validateError.total ? t('mandatoryField') : ''}
                    />
                    <TextField
                        sx={inputStyles.secondary}
                        label={t('vat')}
                        value={vat}
                        onChange={(e) => setVat(e.target.value)}
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <PercentIcon
                                        sx={{
                                            width: '23px',
                                            aspectRatio: 1,
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                        error={validateError.vat}
                        helperText={validateError.vat ? t('mandatoryField') : ''}
                    />
                </Stack>
                <Stack>
                    <Stack direction='row' spacing={isMobile ? 1.5 : 3} justifyContent={isMobile ? 'center' : 'space-between'} alignItems='center'>
                        <TextField
                            sx={inputStyles.secondary}
                            label={t('quantity')}
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                            }}
                                        >
                                            {typeUnits(type)}
                                        </Typography>
                                    </InputAdornment>
                                ),
                            }}
                            error={validateError.quantity}
                            helperText={validateError.quantity ? t('mandatoryField') : ''}
                        />
                        {(category[0] === categories.FUEL || category[0] === categories.CONSUMABLES) ? (
                            <>
                                <TextField
                                    sx={inputStyles.secondary}
                                    label={t(GetLabel(type))}
                                    value={unitPrice.replace('.', ',')}
                                    error={validateError.unitPrice}
                                    helperText={validateError.unitPrice ? t('mandatoryField') : ''}
                                    required
                                    disabled
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                    }}
                                                >
                                                    €/
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                    }}
                                                >
                                                    {typeUnits(type)}
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </>
                        ) : (
                            <>
                                <TextField
                                    sx={inputStyles.secondary}
                                    label={t('unitPrice')}
                                    value={unitPrice.replace('.', ',')}
                                    error={validateError.unitPrice}
                                    helperText={validateError.unitPrice ? t('mandatoryField') : ''}
                                    required
                                    disabled
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                    }}
                                                >
                                                    €
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </LocalizationProvider>
    );
}

export default ReceiptForm;
